import type { AgentTaskModel } from "@fencyai/js";
import { useAgentTasks } from "@fencyai/react";
import { useCallback } from "react";
import type {
  AgentDocumentOwnerDto,
  AgentDocumentSearchScopeDto,
  AgentDocumentSearchSessionDto,
  AgentDocumentSourcesDto,
} from "../../autogen/bff-api";
import {
  AgentDocumentScopeTypeDto,
  useResolveAgentDocumentSourcesMutation,
} from "../../autogen/bff-api";
import type {
  DocumentSearchScope,
  DocumentSource,
  DocumentSourceOwner,
} from "./agentTypes";
import {
  AI_NOT_ALLOWED,
  AI_NOT_ALLOWED_NOTE,
  isAiNotAllowed,
} from "../ai/aiNotAllowed";
import { useRumError } from "../errors/RumComponentContext/useRumError";
import { isAssistantNotIncluded } from "./assistantAccess";
import {
  documentSearchNotIncluded,
  TOOL_MODULE_NOT_INCLUDED,
} from "./enabledTools";
import type { PageEntity, PageEntityKind } from "./pageContext";
import type { ToolResult } from "./tools";

const SEARCH_MODEL: AgentTaskModel = "anthropic/claude-sonnet-4.6";
const QUERY_EXPANSION_FOUND_NOTHING = /query expansion produced no queries/i;
export const SEARCH_TIMEOUT_MS = 45_000;

// Structural subset of the SDK's MemorySearchResultItem (not exported from @fencyai/js).
type MemorySearchHit = {
  memoryId: string;
  memoryTitle: string;
  matchingChunk: { pageNumbers: number[] };
  chunks: Array<{ content: string }>;
};

export type DocumentSearchOutcome = {
  result: ToolResult;
  sources: DocumentSource[];
};

export type DocumentSearch = (
  query: string,
  scope: DocumentSearchScope,
) => Promise<DocumentSearchOutcome>;

type UseDocumentSearchProps = {
  createDocSearchSession: (
    scope: AgentDocumentSearchScopeDto | null,
  ) => Promise<AgentDocumentSearchSessionDto>;
  currentPageEntity: () => PageEntity | null;
  searchesWholeArchive?: boolean;
};

export const ARCHIVE_SEARCH_NOT_INCLUDED = "ARCHIVE_SEARCH_NOT_INCLUDED";

const ARCHIVE_SEARCH_NOTE =
  "Søk i hele dokumentarkivet (alle kontrakter, anskaffelser og vedlegg) er tillegget Kontraktsøk, som organisasjonen ikke har. " +
  "Si kort at søk på tvers av alle dokumenter er tilgjengelig som tillegg, og at du kan søke i dokumentene til en kontrakt eller anskaffelse når brukeren har den åpen. " +
  "Ikke prøv å svare på en annen måte, for eksempel ved å åpne kontrakter eller anskaffelser én etter én.";

const isArchiveRefusal = (error: unknown): boolean =>
  (error as { data?: { errorCode?: unknown } } | null)?.data?.errorCode ===
  "zz_AgentApiErrorCode_ArchiveSearchNotIncluded";

const SCOPE_TYPES: Partial<Record<PageEntityKind, AgentDocumentScopeTypeDto>> =
  {
    contract: AgentDocumentScopeTypeDto.Contract,
    v2Event: AgentDocumentScopeTypeDto.SourcingEventV2,
  };

const pageScope = (
  entity: PageEntity | null,
): AgentDocumentSearchScopeDto | null => {
  const entityType = entity && SCOPE_TYPES[entity.kind];
  return entityType && entity ? { entityType, entityId: entity.id } : null;
};

const bidsScope = (
  entity: PageEntity | null,
): AgentDocumentSearchScopeDto | null =>
  entity?.kind === "v2Event"
    ? {
        entityType: AgentDocumentScopeTypeDto.SourcingEventV2Bids,
        entityId: entity.id,
      }
    : null;

const isModuleRefusal = (error: unknown): boolean =>
  isAssistantNotIncluded(error) ||
  (error as { data?: { errorCode?: unknown } } | null)?.data?.errorCode ===
    TOOL_MODULE_NOT_INCLUDED;

const failed = (
  deviationCode: string,
  message: string,
): DocumentSearchOutcome => ({
  result: { ok: false, deviationCode, message },
  sources: [],
});

const withTimeout = async <T>(
  work: Promise<T>,
  ms: number,
): Promise<T | "timeout"> => {
  let handle: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<"timeout">((resolve) => {
    handle = setTimeout(() => resolve("timeout"), ms);
  });
  try {
    return await Promise.race([work, timeout]);
  } finally {
    clearTimeout(handle);
  }
};

const nothingSearchableMessage = (
  pending: number,
  unreadable: number,
): string => {
  const left =
    unreadable > 0
      ? ` ${unreadable} kunne ikke leses og er ikke med i søket.`
      : "";
  if (pending > 0) {
    return `Ingen av dokumentene er søkbare ennå — ${pending} indekseres fortsatt. Prøv igjen om litt.${left}`;
  }
  if (unreadable > 0) {
    return `Ingen av dokumentene kunne søkes i — ${unreadable} kunne ikke leses.`;
  }
  return "Enheten har ingen indekserte dokumenter å søke i.";
};

const plannerHits = (hits: MemorySearchHit[]) => ({
  hits: hits.map((hit) => ({
    document: hit.memoryTitle,
    memoryId: hit.memoryId,
    pages: hit.matchingChunk.pageNumbers,
    excerpt: hit.chunks.map((chunk) => chunk.content).join(""),
  })),
});

const pagesOf = (hits: MemorySearchHit[], memoryId: string): number[] =>
  [
    ...new Set(
      hits
        .filter((hit) => hit.memoryId === memoryId)
        .flatMap((hit) => hit.matchingChunk.pageNumbers),
    ),
  ].sort((a, b) => a - b);

type ResolveSourcesTrigger = (args: {
  resolveAgentDocumentSourcesRequest: { memoryIds: string[] };
}) => {
  unwrap: () => Promise<AgentDocumentSourcesDto>;
};

// Scope types the cards can navigate to; anything else (or a future enum value) degrades to a
// download-only card instead of a broken link.
const OWNER_TYPES: Partial<Record<string, DocumentSourceOwner["entityType"]>> =
  {
    CONTRACT: "CONTRACT",
    SOURCING_EVENT_V2: "SOURCING_EVENT_V2",
  };

const toOwner = (
  owner: AgentDocumentOwnerDto | null | undefined,
): DocumentSourceOwner | undefined => {
  const entityType = owner && OWNER_TYPES[owner.entityType];
  return owner && entityType
    ? { entityType, entityId: owner.entityId, entityTitle: owner.entityTitle }
    : undefined;
};

// Best-effort enrichment: a resolution failure degrades the cards, never the search itself.
const resolvedSources = async (
  hits: MemorySearchHit[],
  resolveSources: ResolveSourcesTrigger,
): Promise<DocumentSource[]> => {
  const memoryIds = [...new Set(hits.map((hit) => hit.memoryId))];
  if (memoryIds.length === 0) {
    return [];
  }
  try {
    const { sources } = await resolveSources({
      resolveAgentDocumentSourcesRequest: { memoryIds },
    }).unwrap();
    return sources.map((source) => ({
      memoryId: source.memoryId,
      documentName: source.documentName,
      downloadUrl: source.downloadUrl,
      pages: pagesOf(hits, source.memoryId),
      owner: toOwner(source.owner),
    }));
  } catch {
    return [];
  }
};

export const useDocumentSearch = ({
  createDocSearchSession,
  currentPageEntity,
  searchesWholeArchive = true,
}: UseDocumentSearchProps): DocumentSearch => {
  const { createAgentTask } = useAgentTasks({});
  const [resolveSources] = useResolveAgentDocumentSourcesMutation();
  const rumError = useRumError();

  return useCallback(
    async (query, scope) => {
      const trimmed = query.trim();
      if (!trimmed) {
        return failed("EMPTY_QUERY", "Tomt søk.");
      }
      const scopeDto =
        scope === "page"
          ? pageScope(currentPageEntity())
          : scope === "bids"
            ? bidsScope(currentPageEntity())
            : null;
      const fallback = searchesWholeArchive
        ? ' — søk heller med scope "organization".'
        : `. ${ARCHIVE_SEARCH_NOTE}`;
      if (scope === "page" && !scopeDto) {
        return failed("NO_PAGE_ENTITY", `Siden brukeren står på har ingen enhet med dokumenter${fallback}`);
      }
      if (scope === "bids" && !scopeDto) {
        return failed("NO_PAGE_ENTITY", `Scope "bids" krever at brukeren står på en anskaffelse${fallback}`);
      }
      if (!scopeDto && !searchesWholeArchive) {
        return failed(ARCHIVE_SEARCH_NOT_INCLUDED, ARCHIVE_SEARCH_NOTE);
      }
      try {
        const session = await createDocSearchSession(scopeDto);
        const pending = session.pendingDocumentCount ?? 0;
        const unreadable = session.failedDocumentCount ?? 0;
        if (!session.clientToken) {
          return failed(
            "NO_INDEXED_DOCUMENTS",
            nothingSearchableMessage(pending, unreadable),
          );
        }
        const runSearch = (expandQuery: boolean, clientToken: string) =>
          withTimeout(
            createAgentTask(
              {
                type: "MemorySearch",
                query: trimmed,
                model: SEARCH_MODEL,
                language: "no",
                chunkLimit: 5,
                ...(expandQuery ? { queryExpansion: { queryCount: 3 } } : {}),
              },
              {
                fetchCreateAgentTaskClientToken: async () => ({ clientToken }),
              },
            ),
            SEARCH_TIMEOUT_MS,
          );
        let outcome = await runSearch(true, session.clientToken);
        if (
          outcome !== "timeout" &&
          outcome.type !== "success" &&
          QUERY_EXPANSION_FOUND_NOTHING.test(outcome.error.message)
        ) {
          const retrySession = await createDocSearchSession(scopeDto);
          if (retrySession.clientToken) {
            outcome = await runSearch(false, retrySession.clientToken);
          }
        }
        if (outcome === "timeout") {
          return failed("SEARCH_TIMEOUT", "Dokumentsøket brukte for lang tid.");
        }
        if (
          outcome.type !== "success" ||
          outcome.response.taskType !== "MemorySearch"
        ) {
          const message =
            outcome.type !== "success"
              ? outcome.error.message
              : "Uventet svar fra dokumentsøket.";
          rumError(new Error(`Document search failed: ${message}`), {
            documentSearch: {
              scope,
              entityType: scopeDto?.entityType,
              entityId: scopeDto?.entityId,
            },
          });
          return failed("FENCY_ERROR", message);
        }
        const hits = outcome.response.response.items;
        return {
          result: {
            ok: true,
            data: {
              ...plannerHits(hits),
              ...(pending > 0 ? { documentsStillIndexing: pending } : {}),
              ...(unreadable > 0 ? { documentsNotSearched: unreadable } : {}),
            },
          },
          sources: await resolvedSources(hits, resolveSources),
        };
      } catch (e) {
        if (isArchiveRefusal(e)) {
          return failed(ARCHIVE_SEARCH_NOT_INCLUDED, ARCHIVE_SEARCH_NOTE);
        }
        if (isModuleRefusal(e)) {
          return { result: documentSearchNotIncluded, sources: [] };
        }
        if (isAiNotAllowed(e)) {
          return failed(AI_NOT_ALLOWED, AI_NOT_ALLOWED_NOTE);
        }
        return failed("FENCY_ERROR", (e as Error).message);
      }
    },
    [
      searchesWholeArchive,
      createAgentTask,
      createDocSearchSession,
      currentPageEntity,
      resolveSources,
      rumError,
    ],
  );
};
